import type { AuthedRequester } from "@/lib/brokerAuth";
import { formatBondCurrency } from "@/lib/bondValueDisplay";
import { BID_RESULT_FACTS, type BidResultFact } from "@/lib/bondStateModel";

// BRK-S329 — "Request more capacity", consuming PMS-S723.
//
// Decided 2026-10-07 (Aaron, plan rule 16): capacity is freed by a MANUAL
// document upload in the MVP. Two routes: a WIP report on a bonded job (the
// project picked from the orders holding capacity), or a special request. Either
// goes to the underwriter's decision page (PMS); the broker sees "under review"
// while it is open and the decision on the account page afterwards — the new
// limits, or "Request not approved" with NO reason (plan rule 25).
//
// The route and the account read's `capacityRequest` block were typed by hand
// to the shapes proposed to main on 2026-10-07; the sprint-10 release re-pin
// publishes both as written.

export const CAPACITY_REQUEST_KINDS = [
  "capacity_wip",
  "capacity_special",
] as const;
export type CapacityRequestKind = (typeof CAPACITY_REQUEST_KINDS)[number];

export const CAPACITY_REQUEST_STATUSES = [
  "pending",
  "approved",
  "denied",
] as const;
export type CapacityRequestStatus = (typeof CAPACITY_REQUEST_STATUSES)[number];

/** The latest capacity request, as the broker account read publishes it. Open
 *  strings on the wire; `readCapacityRequest` narrows. No reason, ever. */
export interface CapacityRequestWire {
  id?: string;
  kind?: string;
  status?: string;
  submittedAt?: string | null;
  decidedAt?: string | null;
  /** The order the request is for, so the aggregate bar can mark its segment
   *  (BRK-S400). PMS-S844 (P9) — `capacityRequest.bondSubmissionId`. */
  bondSubmissionId?: string | null;
  /** The capacity credit asked for, a plain number (D-10).
   *  PMS-S844 (P9) — `capacityRequest.amount`. */
  amount?: number | null;
  /** PMS's estimate of the capacity a pay application puts back, a plain
   *  number (D-10); the underwriter sets the final amount (BRK-S404).
   *  PMS-S849 (P14) — `capacityRequest.estimatedRelease`. */
  estimatedRelease?: number | null;
}

export interface CapacityRequestView {
  /** `null` for a status this build has not adopted. */
  status: CapacityRequestStatus | null;
  submittedAt: string | null;
  decidedAt: string | null;
  /** null on an older PMS, or a request not tied to one order. Always set by
   *  `readCapacityRequest`; optional so a view built by hand stays valid. */
  bondSubmissionId?: string | null;
  /** null on an older PMS; never a formatted string (D-10). */
  amount?: number | null;
  /** PMS's estimate for a pay application; null when it served none. Never
   *  computed here (NN #7) — the release rule is PMS's (BRK-S404). */
  estimatedRelease?: number | null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/** A plain number from the wire, or null — a formatted string is never
 *  accepted (D-10). */
function amountOf(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** The block, read — or `null` when PMS sent none (no line at all). */
export function readCapacityRequest(raw: unknown): CapacityRequestView | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const status = (CAPACITY_REQUEST_STATUSES as readonly unknown[]).includes(
    row.status,
  )
    ? (row.status as CapacityRequestStatus)
    : null;
  return {
    status,
    submittedAt: text(row.submittedAt),
    decidedAt: text(row.decidedAt),
    bondSubmissionId: text(row.bondSubmissionId),
    amount: amountOf(row.amount),
    estimatedRelease: amountOf(row.estimatedRelease),
  };
}

/** The POST's 201, which carries the request as `capacityRequest` (the pinned
 *  shape) — or null when the body holds none. */
export function readFiledCapacityRequest(
  raw: unknown,
): CapacityRequestView | null {
  if (typeof raw !== "object" || raw === null) return null;
  return readCapacityRequest(
    (raw as { capacityRequest?: unknown }).capacityRequest,
  );
}

/**
 * The ONE document type each route files, mirrored from PMS-S723 (labels in
 * `brokerDocumentTypes.ts`). One type per route, so there is no list to pick
 * from and nothing hardcoded beyond the route's own name for what it collects.
 */
export const CAPACITY_REQUEST_DOCUMENT_TYPE: Record<
  CapacityRequestKind,
  string
> = {
  capacity_wip: "wip_report",
  capacity_special: "capacity_request_document",
};

export interface CapacityRequestInput {
  kind: CapacityRequestKind;
  /** The bonded project (the order's pointer id) — WIP only. */
  bondSubmissionId?: string;
  /** Optional on the wire (nullable in the pin); the screen requires it. */
  notes?: string;
  documentIds: string[];
  // BRK-S404 — the AIA G702 figures a pay application carries (capacity_wip
  // only), beside its document. All optional, so the screen's WIP report
  // without them is unchanged.
  /** A whole number (PMS-S849). */
  applicationNumber?: number;
  /** ISO YYYY-MM-DD on the wire (D-10, PMS-S849). */
  periodTo?: string;
  /** G702 line 3, a plain number (D-10, PMS-S849). */
  contractSum?: number;
  /** G702 line 4, a plain number (D-10, PMS-S849). */
  completedToDate?: number;
}

// PROJECTS CONSUME CAPACITY, NOT BONDS (Aaron, 2026-10-14). The WIP route names
// an order HOLDING capacity on the account — one per order, so a P&P pair is
// one project and a released order is none. Issued bonds were the wrong list.

/** What an order's capacity hold is in, as PMS publishes it. */
export const CAPACITY_PROJECT_STATES = ["open", "locked"] as const;
export type CapacityProjectState = (typeof CAPACITY_PROJECT_STATES)[number];

/** One order holding capacity — `GET …/capacity-projects` (PMS #589), pinned
 *  at the sprint-10 release re-pin (`test/lib/capacityRequest.test.ts`). */
export interface CapacityProject {
  /** The order's pointer id — sent as the request's `bondSubmissionId`. */
  orderId: string;
  orderNumber: string;
  projectName: string | null;
  /** Whole USD on the wire (D-10): formatted only for display. */
  amount: number;
  basis: string;
  state: CapacityProjectState;
  /**
   * Where the order's bid stands (BRK-S442): `none` until a result is
   * recorded, then `low_bidder`, `won` or `lost` — read off the order, so a
   * low-bidder segment says so after a reload. null for an outcome this build
   * has not adopted (a result IS on file, so it is never read as `none`), and
   * for PMS's own null. PMS-S844 (P9) — `capacity-projects[].bidResult`.
   */
  bidResult: BidResultFact | null;
  /**
   * This order's LATEST pay application (BRK-S451), or null for none filed.
   * Per order, so each P&P row of the aggregate bar says where its own stands
   * — the account read publishes only the one latest capacity request.
   * PMS-S849 (P14) — `projects[].payApplication`.
   */
  payApplication: CapacityProjectPayApplication | null;
}

/** The pay application statuses PMS publishes. Read as an open string: a
 *  status this build has not adopted renders a neutral line. */
export const PAY_APPLICATION_STATUSES = [
  "pending",
  "approved",
  "denied",
] as const;
export type PayApplicationStatus = (typeof PAY_APPLICATION_STATUSES)[number];

/** PMS-S849 (P14). Money is a plain number (D-10); the release is PMS's rule
 *  — `estimatedRelease` while pending, `releasedAmount` once approved, each
 *  null when PMS serves none. When it was filed (`submittedAt`) and decided
 *  (`decidedAt`, null while pending) are ISO datetimes (BRK-S511). Never a
 *  reason. */
export interface CapacityProjectPayApplication {
  applicationNumber: number;
  status: string;
  submittedAt: string | null;
  decidedAt: string | null;
  estimatedRelease: number | null;
  releasedAmount: number | null;
}

/** The refusal PMS answers when the named project holds no capacity. */
export const PROJECT_NOT_HOLDING_CAPACITY = "PROJECT_NOT_HOLDING_CAPACITY";

/**
 * GET /api/broker/accounts/{id}/capacity-projects — the orders holding capacity
 * on the account. PMS already excludes released orders; a state this build
 * cannot read is dropped too, so the select never offers a project it would
 * mislabel.
 */
export async function listCapacityProjects(
  request: AuthedRequester,
  accountId: string,
): Promise<CapacityProject[]> {
  const body = await request<{ projects?: unknown }>(
    `/api/broker/accounts/${encodeURIComponent(accountId)}/capacity-projects`,
  );
  const rows: unknown[] = Array.isArray(body.projects) ? body.projects : [];
  return rows
    .filter(
      (
        row,
      ): row is Omit<CapacityProject, "bidResult" | "payApplication"> & {
        bidResult?: unknown;
        payApplication?: unknown;
      } =>
        typeof row === "object" &&
        row !== null &&
        typeof (row as { orderId?: unknown }).orderId === "string" &&
        (CAPACITY_PROJECT_STATES as readonly unknown[]).includes(
          (row as { state?: unknown }).state,
        ),
    )
    .map((row) => ({
      ...row,
      bidResult: readProjectBidResult(row.bidResult),
      payApplication: readPayApplication(row.payApplication),
    }));
}

/** A row's bid-result fact (BRK-S442). Tolerant: an unknown value is null —
 *  neutral, never a crash and never a false `none`. */
export function readProjectBidResult(raw: unknown): BidResultFact | null {
  return BID_RESULT_FACTS.find((fact) => fact === raw) ?? null;
}

/** A served pay application, narrowed from the wire (NN #7): one without a
 *  whole application number is not one this build can name. Figures that are
 *  not plain numbers read as null (D-10). */
export function readPayApplication(
  raw: unknown,
): CapacityProjectPayApplication | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (!Number.isInteger(r.applicationNumber)) return null;
  const money = (v: unknown) =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  const when = (v: unknown) =>
    typeof v === "string" && v.trim() !== "" ? v : null;
  return {
    applicationNumber: r.applicationNumber as number,
    status: typeof r.status === "string" ? r.status : "",
    submittedAt: when(r.submittedAt),
    decidedAt: when(r.decidedAt),
    estimatedRelease: money(r.estimatedRelease),
    releasedAmount: money(r.releasedAmount),
  };
}

/** "SB-10000 — New Construction Building · $100,000 locked". */
export function capacityProjectLabel(project: CapacityProject): string {
  const held = project.state === "locked" ? "locked" : "reserved";
  return `${project.orderNumber} — ${
    project.projectName ?? "(no project name)"
  } · ${formatBondCurrency(project.amount)} ${held}`;
}

/** What the screen says once PMS answered the POST with a 2xx — and only then. */
export const CAPACITY_REQUEST_FILED = "Your request is with your underwriter.";

/** Where a capacity request starts, for an account. */
export function capacityRequestHref(accountId: string): string {
  return `/surety/accounts/${encodeURIComponent(accountId)}/capacity-request`;
}

/**
 * POST /api/broker/accounts/{id}/capacity-requests — file the request.
 * 201 with `{capacityRequest}`, read here; 409 when one is already open for
 * that kind (PMS's sentence renders inline). Pinned at the sprint-10 release
 * re-pin; the pay application's fields since BRK-S511 (PMS-S849).
 */
export async function submitCapacityRequest(
  request: AuthedRequester,
  accountId: string,
  input: CapacityRequestInput,
): Promise<CapacityRequestView | null> {
  const body = await request<unknown>(
    `/api/broker/accounts/${encodeURIComponent(accountId)}/capacity-requests`,
    { method: "POST", body: input },
  );
  return readFiledCapacityRequest(body);
}
